import {
  createStyles,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Theme,
  useTheme,
} from '@material-ui/core';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import VoxelDialog from '../../../../components/voxel/Dialogs/VoxelDialog';
import VoxelDialogActionCancel from '../../../../components/voxel/Dialogs/VoxelDialogActionCancel';
import VoxelDialogActions from '../../../../components/voxel/Dialogs/VoxelDialogActions';
import VoxelDialogActionSubmit from '../../../../components/voxel/Dialogs/VoxelDialogActionSubmit';
import VoxelDialogContent from '../../../../components/voxel/Dialogs/VoxelDialogContent';
import VoxelText from '../../../../components/voxel/Text/VoxelText';
import SimpleSearch from '../../../../components/muiLegacy/TextFieldSearch/SimpleSearch';
import { ContentKeywordGq } from '../../../../graphql/projects/models/ContentKeywordGq';
import { ContentTaskGq } from '../../../../graphql/projects/models/ContentTaskGq';
import { projectsApiService } from '../../../../graphql/projects/projectsApiService';
import { formatNumber } from '../../../../logic/otherUtilities/formatting/formatNumber';
import { useDebounceValue } from '../../../../logic/otherUtilities/useDebounceValue';
import { ContentTaskTableState } from '../Tables/ContentKeywordTable/logic/useContentTaskTableState';
import TaskUrl from '../Tables/ContentTaskTable/TaskUrl';

export interface ContentKeywordsExistingUrlDialogProps {
  clientUrl: string;
  selectActive: boolean;
  contentTask: ContentTaskGq;
  selectedCount: number;
  selectedKeywordIds: number[];
  tableState: ContentTaskTableState;
  setLocalItems: Dispatch<SetStateAction<ContentKeywordGq[]>>;
  reload: () => Promise<any>;
  openDialog: boolean;
  setOpenDialog: Dispatch<SetStateAction<boolean>>;
}

const useClasses = makeStyles((theme: Theme) =>
  createStyles({
    tableRow: {
      cursor: 'pointer',
    },
    tableRowSelected: {
      cursor: 'pointer',
      backgroundColor: theme.palette.grey[200],
    },
  })
);

const ContentKeywordsExistingUrlDialog: React.FC<ContentKeywordsExistingUrlDialogProps> = ({
  clientUrl,
  selectActive,
  contentTask,
  selectedCount,
  selectedKeywordIds,
  tableState,
  setLocalItems,
  reload,
  openDialog,
  setOpenDialog,
}) => {
  const [updating, setUpdating] = useState(false);

  // Url move
  const [urlMoveSearchTerm, setUrlMoveSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  useEffect(() => {
    if (!selectActive) {
      close();
    }
  }, [selectActive]);

  // Mutation
  const debouncedSearchTerm = useDebounceValue(urlMoveSearchTerm, 200);

  const existingUrlsQuery = projectsApiService.queries.useGetExistingUrls({
    searchTerm: debouncedSearchTerm,
    projectId: contentTask.projectId,
    page: page,
    rowsPerPage: 10,
  });

  // Move to existing
  const urlMoveMutation = projectsApiService.mutations.contentPlan.useAddTaskKeywords();
  const handleUrlMoveToExisting = (newTaskId: number) => {
    setUpdating(true);
    setLocalItems((prev) => prev.filter((x) => !selectedKeywordIds.includes(x.id)));
    urlMoveMutation({
      projectId: contentTask.projectId,
      sourceContentKeywordIds: selectedKeywordIds,
      destinationContentTaskId: newTaskId,
      destinationOrderKeywordIds: [],
      insertByVolumeOrder: true,
    })
      .then(() => reload())
      .finally(() => {
        setUpdating(false);
        setUrlMoveSearchTerm('');
        tableState.handleResetSelection();
        setOpenDialog(false);
      });
  };

  // Move to new
  const urlNewMutation = projectsApiService.mutations.contentPlan.useSplitToSingleCreateTask();
  const handleUrleMoveToNew = (url: string) => {
    setUpdating(true);
    setLocalItems((prev) => prev.filter((x) => !selectedKeywordIds.includes(x.id)));
    urlNewMutation({
      contentKeywordIds: selectedKeywordIds,
      projectId: contentTask.projectId,
      newUrl: url,
      contentTaskId: contentTask.id,
    })
      .then(() => reload())
      .finally(() => {
        setUpdating(false);
        setUrlMoveSearchTerm('');
        tableState.handleResetSelection();
        setOpenDialog(false);
      });
  };

  // Selection
  const [selectedTaskId, setSelectedTaskId] = useState<number>();
  const [selectedUrl, setSelectedUrl] = useState<string>();

  const taskUrl = (contentTaskUrl: string) => {
    const urlIndex = contentTaskUrl.indexOf(clientUrl) + (clientUrl?.length ?? 0);
    const urlSnippet = contentTaskUrl?.substring(Math.max(urlIndex, 0));
    const start = urlSnippet.startsWith('/') || urlSnippet.startsWith('http') ? '' : '/';
    return start + urlSnippet;
  };

  const handleSelectTask = (selectedUrl: string, id?: number) => {
    setSelectedTaskId(id);
    id ? setSelectedUrl(taskUrl(selectedUrl)) : setSelectedUrl(selectedUrl);
  };
  //Styling
  const theme = useTheme();
  const classes = useClasses(theme);

  return (
    <VoxelDialog
      maxWClass={'max-w-5xl'}
      open={openDialog}
      onClose={() => setOpenDialog(false)}
      title={'Move to existing url'}
      subtitle={
        !(selectedTaskId || selectedUrl)
          ? 'No url selected'
          : `Move ${selectedCount} keywords to existing url ${!!selectedUrl ? selectedUrl : ''}?`
      }
    >
      <VoxelDialogContent>
        <div className="flex flex-col gap-4">
          <SimpleSearch
            autoFocus
            disabled={updating}
            searchText={urlMoveSearchTerm}
            setSearchText={(value) => {
              setUrlMoveSearchTerm(value);
              setPage(0);
            }}
            placeholder={'URL search...'}
          />
          <div>
            {existingUrlsQuery?.count === 0 && <VoxelText>No urls found</VoxelText>}
            {existingUrlsQuery?.count >= 1 && (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>URL</TableCell>
                        <TableCell align={'right'}>Keyword count</TableCell>
                        <TableCell align={'right'}>Total volume</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {existingUrlsQuery.urls?.map((existingUrl) =>
                        existingUrl.contentTaskId === contentTask.id ? null : (
                          <TableRow
                            key={existingUrl.url}
                            hover={!updating}
                            className={
                              selectedUrl === existingUrl.url ||
                                (selectedTaskId === existingUrl.contentTaskId &&
                                  existingUrl.contentTaskId)
                                ? classes.tableRowSelected
                                : classes.tableRow
                            }
                            onClick={() =>
                              handleSelectTask(existingUrl.url, existingUrl.contentTaskId)
                            }
                          >
                            <TableCell>
                              <TaskUrl contentTaskUrl={existingUrl.url} clientUrl={clientUrl} />
                            </TableCell>
                            <TableCell align={'right'}>
                              {formatNumber(existingUrl.contentKeywordCount)}
                            </TableCell>
                            <TableCell align={'right'}>
                              {formatNumber(existingUrl.contentKeywordVolumeSum)}
                            </TableCell>
                          </TableRow>
                        )
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component={'div'}
                  count={existingUrlsQuery.count ?? 0}
                  page={existingUrlsQuery.page ?? 0}
                  rowsPerPage={existingUrlsQuery.rowsPerPage}
                  rowsPerPageOptions={[]}
                  onPageChange={(event, page) => setPage(page)}
                  onRowsPerPageChange={(event) => { }}
                />
              </>
            )}
          </div>
        </div>
      </VoxelDialogContent>

      <VoxelDialogActions>
        <VoxelDialogActionCancel onClick={() => setOpenDialog(false)} />
        <VoxelDialogActionSubmit
          title={'Move to existing url'}
          onClick={() => {
            if (selectedTaskId) {
              handleUrlMoveToExisting(selectedTaskId);
            } else {
              handleUrleMoveToNew(selectedUrl);
            }
          }}
          disabled={updating || (!selectedTaskId && !selectedUrl)}
        />
      </VoxelDialogActions>
    </VoxelDialog>
  );
};

export default ContentKeywordsExistingUrlDialog;
